/**
 * Where a signed-out visitor was going. The owner notice and the alert emails
 * link straight to a page in Admin (Warren, 28 Oct 2026, item 6, step 4: "a
 * link to that location's contact list in Admin", /locations/:id/facilities),
 * and an email link usually opens Admin signed out. The sign-in page renders
 * at that URL; without this every sign-in (password, magic link, MFA) landed
 * on the role's home instead.
 *
 * App.tsx remembers the path of a load that started signed out (never a tab
 * that was signed in and then out, so signing out and back in still lands on
 * the home). Each sign-in surface takes it once.
 *
 * localStorage, not sessionStorage: the magic link the visitor asks for opens
 * in a new tab. Only a same-origin path is kept or followed: it starts with a
 * single slash, holds no backslash, whitespace or control character, no query
 * or fragment (a token-bearing link, such as an invite's or a reset's, never
 * reaches storage), and is not the root, a sign-in page or the Sales Partner
 * invite. It is used once and expires after
 * RETURN_PATH_TTL_MS. Storage that throws (a private window, blocked site
 * data) means the role's home, as before.
 */

const KEY = 'evi.admin.return_path';
export const RETURN_PATH_TTL_MS = 30 * 60_000;
const SIGN_IN_PAGES = /^\/(?:login|auth|sales-partner-invite)(?:\/|$)/;

export interface ReturnPathStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function browserStore(): ReturnPathStore | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** A path this app may send a visitor to after sign-in. */
export function isReturnPath(path: unknown): path is string {
  if (typeof path !== 'string' || path.length < 2 || path.length > 512) return false;
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  for (const ch of path) {
    const code = ch.charCodeAt(0);
    if (code <= 0x20 || code === 0x7f || ch === '\\' || ch === '?' || ch === '#') return false;
  }
  return !SIGN_IN_PAGES.test(path);
}

export function rememberReturnPath(path: string, now = Date.now(), store: ReturnPathStore | null = browserStore()): void {
  if (!store || !isReturnPath(path)) return;
  try {
    store.setItem(KEY, JSON.stringify({ path, at: now }));
  } catch {
    // Storage blocked: the role's home after sign-in, as before.
  }
}

/** The remembered path, once: read and cleared; null when absent, stale or unsafe. */
export function takeReturnPath(now = Date.now(), store: ReturnPathStore | null = browserStore()): string | null {
  if (!store) return null;
  let raw: string | null;
  try {
    raw = store.getItem(KEY);
    store.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof v.at !== 'number' || now - v.at > RETURN_PATH_TTL_MS || v.at - now > 60_000) return null;
    return isReturnPath(v.path) ? v.path : null;
  } catch {
    return null;
  }
}
